import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  GetNotificationsQuery,
  NotificationResponse,
  NotificationsClient,
  NotificationType,
} from '../model/fss.api';
import {
  NotificationIcon,
  NotificationItem,
} from '../components/notifications/notifications';
import { getRelativeTimeSr } from './date.service';
import { NOTIFICATIONS_PAGE_SIZE } from '../constants';

const ICON_BY_TYPE: Record<NotificationType, NotificationIcon> = {
  [NotificationType.NEW_PACKAGE_REQUEST]: 'request',
  [NotificationType.MISSING_FILE]: 'warning',
  [NotificationType.INVOICE_SENT]: 'invoice',
  [NotificationType.PACKAGE_ACTIVATED]: 'package',
  [NotificationType.APPOINTMENT_REMINDER]: 'reminder',
  [NotificationType.REQUEST_PROCESSING]: 'processing',
};

interface NotificationsPage {
  currentPage: number;
  pageSize: number;
  totalCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private _client = inject(NotificationsClient);

  private _lastUnreadOnly = false;

  items = signal<NotificationItem[]>([]);
  unreadCount = signal<number>(0);

  paginatedData = signal<NotificationsPage>({
    currentPage: 1,
    pageSize: NOTIFICATIONS_PAGE_SIZE,
    totalCount: 0,
  });

  first = computed(() => {
    const page = this.paginatedData();
    return (page.currentPage - 1) * page.pageSize;
  });

  async loadNotifications(unreadOnly = false, pageNumber = 1): Promise<void> {
    this._lastUnreadOnly = unreadOnly;

    // fromJS instead of the constructor: with target ES2022 the subclass field `unreadOnly`
    // is re-initialised to undefined after super() copies the data, so it would never be sent.
    const query = GetNotificationsQuery.fromJS({
      unreadOnly,
      pageNumber,
      pageSize: NOTIFICATIONS_PAGE_SIZE,
    });
    const res = await firstValueFrom(this._client.getForUser(query));
    const data = res.result?.data ?? [];

    this.items.set(data.map((n) => this._toItem(n)));
    this.paginatedData.set({
      currentPage: res.result?.currentPage ?? pageNumber,
      pageSize: res.result?.pageSize ?? NOTIFICATIONS_PAGE_SIZE,
      totalCount: res.result?.totalCount ?? 0,
    });
  }

  onPageChange(event: { first: number; rows: number }): void {
    const newPage = Math.floor(event.first / event.rows) + 1;
    this.loadNotifications(this._lastUnreadOnly, newPage);
  }

  async loadUnreadCount(): Promise<void> {
    const res = await firstValueFrom(this._client.getUnreadCount());
    this.unreadCount.set(res.result ?? 0);
  }

  async markAsRead(id: string): Promise<void> {
    const wasUnread = this.items().find((i) => i.id === id)?.unread ?? false;

    await firstValueFrom(this._client.markAsRead(id));

    this.items.update((list) =>
      list.map((i) => (i.id === id ? { ...i, unread: false } : i)),
    );
    if (wasUnread) {
      this.unreadCount.update((count) => Math.max(0, count - 1));
    }
  }

  async markAllAsRead(): Promise<void> {
    await firstValueFrom(this._client.markAllAsRead());

    this.items.update((list) => list.map((i) => ({ ...i, unread: false })));
    this.unreadCount.set(0);
  }

  addFromPush(notification: NotificationResponse): void {
    this.items.update((list) => [this._toItem(notification), ...list]);
    this.unreadCount.update((count) => count + 1);
  }

  private _toItem(n: NotificationResponse): NotificationItem {
    return {
      id: n.id!,
      icon: ICON_BY_TYPE[n.type!],
      title: n.title!,
      description: n.body!,
      timeAgo: getRelativeTimeSr(n.createdOn!),
      unread: !n.isRead,
    };
  }
}
